<script setup lang="ts">
import {
    ContentComp,
    DisplayElapsedTime,
    DropdownSelect,
    InputNullableDateTime,
    InputNullableTimespan,
    ListComp,
    ToolbarComp,
} from '@/components';
import ButtonGroup from '@/components/ButtonGroup/ButtonGroup.vue';
import { GridColumnOptions } from '@/components/Grid/Classes';
import type { IGridRowCssRule } from '@/components/Grid/Classes/IGridRowCssRule';
import { useKeyboardListener, useTaskLoader } from '@/composeables';
import { ContestantDao } from '@/dao';
import { DropdownItemButton, type SelectLine } from '@/objects';
import { AddTimeRequest, ContestantAndRaceContestantTime, RaceContestant, type RaceContestantDetails } from '@/objects/generated';
import type ListCompExpose from '@/objects/ListCompExpose';
import { useEventCommonStoreRef, useGuiStore } from '@/stores';
import { computed, ref, watch } from 'vue';
import ContestantTimeWithEditButton from './ContestantTimeWithEditButton.vue';
import { GridColumnOptionsBuilder } from '@/components/Grid/Classes/GridColumnOptions';
import { propertyof } from '@/utilities';
import { AddEditTimeRequest } from '../Time/AddEditTimeRequest';
import { useTransactions } from '@/composeables/transactions/transactions';
import { ThreadSleep } from '@/utilities/utils';
import { arrayGroupbyToMap, arraySlidingWindow } from '@/utilities/arrayUtils';
import AddEditTime from '../Time/AddEditTime.vue';
import { useDialogBuilder } from '@/composeables/dialog/dialogbuilder';
import WellKnown from '@/objects/generated/static/WellKnown';
import { TimeSpan } from '@/objects/TimeSpan';

const listComp = ref<ListCompExpose<ContestantAndRaceContestantTime> | null>(null);

const contestant = defineModel<RaceContestantDetails>({ required: true });

const { AddLoader, LoadState } = useTaskLoader();

const { Stations, StationRoles, Races } = useEventCommonStoreRef();

const data = ref<ContestantAndRaceContestantTime[]>([]);

// The toolbar search string
const searchText = ref<string>('');

// Set the initial value for the contestant
const firstRaceContestant: RaceContestant | null = contestant.value.RaceContestants.length > 0 ? contestant.value.RaceContestants[0] : null;

// The currently selected race contestant
const selectedRaceContestantId = ref<number | null>(firstRaceContestant?.ID ?? null);

const selectedRaceContestant = ref<RaceContestant | null>(firstRaceContestant);

// We create the datasource for the dropdown with the race contestants
const computedRacecontestantsAsSelectline = computed<SelectLine[]>(() => {
    return contestant.value.RaceContestants.map((rc) => {
        return {
            value: rc.ID,
            text: Races.value.find((x) => x.ID == rc.RaceID)?.Name ?? '',
        };
    });
});

/**
 * We return the accumulated time as TimeSpan for the finished time for the currently selected race contestant.
 * @returns {TimeSpan | null} Finish time as TimeSpan or null if we cannot calculate it
 */
const computedRacecontestantFinishTime = computed(() => {
    if (!selectedRaceContestant.value) {
        return null;
    }

    const finishRole = StationRoles.value.find(
        (sr) => sr.Finish && (sr.RaceID == selectedRaceContestant.value?.RaceID || sr.RaceID == null),
    );

    if (!finishRole) {
        return null;
    }

    const finishTime = data.value.find(
        (t) =>
            t.TimeType == 'RaceContestantTime' &&
            t.RaceContestantID == selectedRaceContestant.value?.ID &&
            t.StationRoleID == finishRole.ID,
    );

    if (!finishTime) {
        return null;
    }

    return TimeSpan.FromMilliseconds(finishTime.AccumulatedTime ?? 0);
});

/**
 * We add a filter to filter on used Time only or all times
 * @param row
 * @param searchstringlowercase
 */
const showAllTimesFilter = (row: ContestantAndRaceContestantTime): boolean => {
    if (!showAllTimes.value) {
        return row.TimeType == 'RaceContestantTime';
    } else {
        return true;
    }
};

/**
 * We need to define what values to search in for the search text
 * Since we are mapping in the Station and roles we need to do a bit more work
 * @param row
 * @param searchstringlowercase
 */
const searchTextFilter = (row: ContestantAndRaceContestantTime, searchstringlowercase: string): boolean => {
    if (searchstringlowercase == '') {
        return true;
    }

    // We need to search in all the columns
    return (
        (row.Description?.toLowerCase().includes(searchstringlowercase) ||
            row.TimeReason?.toLowerCase().includes(searchstringlowercase) ||
            Stations.value
                .find((s) => s.ID == row.StationID)
                ?.Name?.toLowerCase()
                .includes(searchstringlowercase) ||
            StationRoles.value
                .find((s) => s.ID == row.StationRoleID)
                ?.Name?.toLowerCase()
                .includes(searchstringlowercase)) ??
        true
    );
};

const splitTimesMap = new Map<number, number>();

// Contains all the station roles that are a lap penalty
const lapPenaltyStationIds: number[] = [];

// contains an ordered list of all the penalty times for lap penalties. We use this to check if a time is a penalty lap time, since these can be different per contestant and are not fixed like the split times
let penaltyLapMap: Map<number, number> = new Map();

/**
 * Load the data for the view. We load all the data from the backend
 */
const LoadData = () => {
    AddLoader(async () => {
        const times = await ContestantDao.GetContestantTimes(contestant.value.EventID, contestant.value.ContestantID);

        // We need a list of times ordered the other way
        const timesOrderedByTime = [...times].sort((a, b) => ((a.Time?.getTime() ?? 0) > (b.Time?.getTime() ?? 0) ? 1 : -1));
        const timesPerRaceContestant = arrayGroupbyToMap(timesOrderedByTime, (x) => x.RaceContestantID);

        lapPenaltyStationIds.push(...Stations.value.filter((s) => s.StationTypeID == WellKnown.STATIONTYPE_PENALTYLAP).map((s) => s.ID));

        for (const [rcId, groupedTimes] of timesPerRaceContestant.entries()) {
            if (rcId == null) {
                continue;
            }
            // We assume we have times sorted by AccumulatedTime

            arraySlidingWindow(groupedTimes, 2).forEach(([current, next]) => {
                if (!current || !next) {
                    return;
                }
                if (
                    current.RaceContestantID === next.RaceContestantID &&
                    current.TimeType === 'RaceContestantTime' &&
                    next.TimeType === 'RaceContestantTime'
                ) {
                    const split = (next.AccumulatedTime ?? 0) - (current.AccumulatedTime ?? 0);
                    splitTimesMap.set(next?.ID, split);
                }
            });
        }

        // We loop through the times and check all the times that are for the station marked as LapPenalty function
        // We create a map with the
        let previousTime: Date | null = null;
        arraySlidingWindow(timesOrderedByTime, 2).forEach(([current, next]) => {
            // If the current time is for a lap penalty we will try and calculate the diff
            if (current && lapPenaltyStationIds.includes(current.StationID ?? -1)) {
                const penaltyTime = previousTime == null ? 0 : previousTime.getTime() - current.Time.getTime();
                penaltyLapMap.set(current.Time.getTime(), penaltyTime * -1);
                previousTime = current.Time;

                if (!next || !lapPenaltyStationIds.includes(next.StationID ?? -1)) {
                    previousTime = null; // We have a penalty lap followed by another penalty lap, we cannot calculate the penalty time, so we reset the previous time
                }
            }
        });

        data.value = times;

        listComp.value?.dataUpdated();
    }, '');
};

LoadData();

/**
 * Callback triggered when the save button in the edit time modal is clicked
 * @param request
 */
const saveNewTime = (request: AddEditTimeRequest) => {
    // We need to disable the previous time and add a new one
    if (request.tuid != null) {
        disableTimes([request.tuid]); // We just fire off this request and do not wait for it to finish
    }

    if (!request.time || !request.raceContestantId || !request.stationId || !contestant.value.ContestantID) {
        return;
    }

    // Add a new time
    AddLoader(async () => {
        let req = AddTimeRequest.FromJsValues({
            ContestantID: contestant.value.ContestantID,
            RaceContestantID: request.raceContestantId!,
            StationID: request.stationId!,
            Time: request.time!,
            ForceFinish: request.forceFinish ?? false,
            ForcedLap: request.forcedLap,
        });

        await useTransactions().AddTimes([req]);

        await ThreadSleep(500); // We give the backend some time to process the transactions since it is async

        LoadData();
    }, '');
};

/**
 * Just a alias for the saveNewTime function
 * @param request
 */
const saveNewTimeCallback = (request: AddEditTimeRequest) => saveNewTime(request);

const handleCustomColumns = (columns: GridColumnOptions<ContestantAndRaceContestantTime>[]) => {
    // We change the cellrenderer for the time column for our custom component that wraps the DateComp with a edit button
    let column = columns.find((c) => c.dataField == propertyof<ContestantAndRaceContestantTime>('Time'));

    if (column != null) {
        // TODO: We never get here, so the ContestantTimeWithEditButton is unused.
        column.cellRenderer = ContestantTimeWithEditButton;
        column.cellRendererParams = { saveCallback: saveNewTimeCallback };
        column.minWidth = 200;
        column.maxWidth = 200;
        column.resizable = false;
    }

    let lapColumn = columns.find((c) => c.dataField == propertyof<ContestantAndRaceContestantTime>('LapNumber'));
    if (lapColumn != null) {
        lapColumn.minWidth = 80;
        lapColumn.maxWidth = 80;
        lapColumn.resizable = false;
    }

    let reasonColumn = columns.find((c) => c.dataField == propertyof<ContestantAndRaceContestantTime>('TimeReason'));
    if (reasonColumn != null) {
        reasonColumn.valueGetter = (params) => {
            if (params.TimeReason == '0: Active') {
                return '';
            }
            return params.TimeReason ?? '';
        };
    }

    // We add a column for accumulated time
    new GridColumnOptionsBuilder<ContestantAndRaceContestantTime>(
        propertyof<ContestantAndRaceContestantTime>('AccumulatedTime'),
        'import.resulttime',
    )
        .ReadOnly()
        .SortDisabled()
        .Size(120, 120)
        .CellRenderer(DisplayElapsedTime)
        .Insert(columns, 6);

    new GridColumnOptionsBuilder<ContestantAndRaceContestantTime>(
        propertyof<ContestantAndRaceContestantTime>('AccumulatedTime'),
        'import.difftime',
        'difftime',
    )
        .ReadOnly()
        .SortDisabled()
        .Size(120, 120)
        .CellRenderer(DisplayElapsedTime)
        .ValueGetter((params) => {
            if (lapPenaltyStationIds.includes(params.StationID ?? -1)) {
                // We find the index of this penalty lap time in the list of times
                return penaltyLapMap.get(params.Time.getTime()) ?? null;
            } else {
                if (splitTimesMap.has(params.ID)) {
                    return splitTimesMap.get(params.ID);
                } else {
                    return null;
                }
            }

            return null;
        })
        .Insert(columns, 7);

    return columns;
};

/**
 * The row class rules for the grid
 * We want to show the current active times as Green and the times that are not active as red. Disabled times we show as grey
 */
const rowClassRules: IGridRowCssRule<ContestantAndRaceContestantTime>[] = [
    {
        className: 'grid-row-error',
        func: (data) => data.TimeType == 'ContestantTime' && !data.Disabled,
    },
    {
        className: 'grid-row-success',
        func: (data) => data.TimeType == 'RaceContestantTime' && data.RaceContestantID == selectedRaceContestantId.value && !data.Disabled,
    },
    {
        className: 'grid-row-warning',
        func: (data) => data.TimeType == 'RaceContestantTime' && data.RaceContestantID != selectedRaceContestantId.value && !data.Disabled,
    },
    {
        className: 'grid-row-disabled',
        func: (data) => data.Disabled,
    },
];

/**
 * Add a new time for the selected contestant
 */
const addNewTime = () => {
    const raceContestant = contestant.value.RaceContestants.find((rc) => rc.ID == selectedRaceContestantId.value);
    // We assume it is finish
    const stationId =
        StationRoles.value.find((s) => (s.RaceID == raceContestant?.RaceID || s.RaceID == null) && s.Finish)?.StationID ?? null;

    const request = ref(new AddEditTimeRequest({ time: null, raceContestantId: raceContestant?.ID, stationId: stationId }));

    // TODO: Use globaldialog OpenAddTimeModal (when that has support for selecting the station ID)
    useDialogBuilder()
        .withTitle('contestanttime.edittime')
        .withBodyComponent(AddEditTime, {
            vmodel: request,
        })
        .withCancelButton({
            action: () => {},
        })
        .withSaveButton({
            action: async () => {
                saveNewTime(request.value);
                return true;
            },
        })
        .withOptions({
            Size: 'md',
        })
        .Open();
};

/**
 * Disable the selected tuids
 * @param tuids
 */
const disableTimes = (tuids: string[]) => {
    AddLoader(async () => {
        await useTransactions().DisableTransactions(tuids);

        await ThreadSleep(500); // We give the backend some time to process the transactions since it is async

        LoadData();
    }, '');
};

/**
 * Enable the selected tuids
 * @param tuids
 */
const enableTimes = (tuids: string[]) => {
    AddLoader(async () => {
        await useTransactions().EnableTransactions(tuids);

        await ThreadSleep(500); // We give the backend some time to process the transactions since it is async

        LoadData();
    }, '');
};

const showAllTimes = ref<boolean>(false);

/**
 * Helper function to get the selected TUIDs
 */
const getSelectedTUIDs = () =>
    listComp.value
        ?.getSelected()
        ?.map((x) => x.TUID)
        ?.filter((x) => x != null) ?? [];

// The toolbar actions
const toolbarActions = [
    DropdownItemButton.create({
        buttonType: 'Primary',
        translationkey: 'contestanttimes.addnewtime',
        title: 'contestanttimes.addnewtimetitle',
        icon: 'fa-plus',
        action: addNewTime,
    }),
    DropdownItemButton.create({
        translationkey: 'contestanttimes.disabletimes',
        title: 'contestanttimes.disabletimestitle',
        icon: 'fa-xmark',
        buttonType: 'Secondary',
        action: () => disableTimes(getSelectedTUIDs()),
    }),
    DropdownItemButton.create({
        translationkey: 'contestanttimes.enabletimes',
        title: 'contestanttimes.endabletimestitle',
        icon: 'fa-check',
        buttonType: 'Secondary',
        action: () => enableTimes(getSelectedTUIDs()),
    }),
];

const toolbarRightActions = computed(() => [
    // Hide devices with no event
    DropdownItemButton.create({
        title: 'contestanttimes.showalltimes',
        icon: !showAllTimes.value ? 'fa-stopwatch' : 'fa-duotone fa-stopwatch',
        class: showAllTimes.value ? 'active' : '',
        buttonType: 'Secondary',
        action: () => {
            showAllTimes.value = !showAllTimes.value;
            listComp.value?.dataUpdated();
        },
    }),
    DropdownItemButton.create({
        title: 'general.refresh',
        icon: 'fa-sync',
        buttonType: 'Secondary',
        action: () => LoadData(),
    }),
]);

/**
 * Callback when the visibility of the component changes
 * @param isVisible
 */
const onIsVisible = (isVisible: boolean) => {
    // We add a temporary keyboard shortcut for CTRL+INSERT to add a new time
    if (isVisible) {
        useKeyboardListener().addKeyboardShortcut({ alias: 'contestanttimesaddnewtime', on: 'CTRL+INSERT', action: addNewTime });
    } else {
        // We make sure we remove the keyboard shortcut when the component is not visible
        useKeyboardListener().removeKeyboardShortcut('contestanttimesaddnewtime');
    }
};

// Just trigger a data updated when we change the race contestants. We do this so we also run the row rules
watch(selectedRaceContestantId, () => {
    // We also switch the selected race contestant
    selectedRaceContestant.value =
        contestant.value.RaceContestants.find((rc) => rc.ID == selectedRaceContestantId.value) ?? selectedRaceContestant.value;
    listComp.value?.dataUpdated();
});

watch(contestant.value.RaceContestants, (nv, ov) => {
    if (selectedRaceContestantId.value == null && nv?.length > 0) {
        selectedRaceContestantId.value = nv[0].ID;
    }
});
</script>
<template>
    <ContentComp
        v-if="contestant && selectedRaceContestantId != null"
        :fill-content="true"
        :height="50"
        :load-state="LoadState"
        class="p-0"
        :show-as-modal="true"
        @is-visible="onIsVisible"
    >
        <ToolbarComp
            :search-box-placeholder="$tm('devicestatus.searchplaceholder')"
            v-model:searchtext="searchText"
        >
            <template #first>
                <label
                    class="form-label textlabel m-0"
                    v-if="computedRacecontestantsAsSelectline.length > 1"
                >
                    <span>{{ $tm('general.race') }}</span>
                    <DropdownSelect
                        :data="computedRacecontestantsAsSelectline"
                        v-model="selectedRaceContestantId"
                    />
                </label>

                <!-- Input for startingtime -->
                <label class="form-label textlabel m-0 me-2">
                    <span>{{ $tm('racecontestantcomplete.startingtime') }}</span>
                    <InputNullableDateTime
                        v-model="selectedRaceContestant!.StartingTime"
                        format="DATETIMEMS3"
                    />
                </label>

                <!-- Input for finishtime -->
                <label class="form-label textlabel m-0 me-5">
                    <span>{{ $tm('racecontestantcomplete.finishtime') }}</span>
                    <InputNullableTimespan
                        v-model="computedRacecontestantFinishTime"
                        format="TIMEMS3"
                        :readonly="true"
                    />
                    <!-- <InputNullableDateTime
                        v-model="selectedRaceContestant!.StartingTime"
                        format="DATETIMEMS3"
                        :disabled="true"
                        :hideSelector="true"
                    /> -->
                </label>

                <ButtonGroup :actions="toolbarActions" />
            </template>
            <template #last>
                <ButtonGroup :actions="toolbarRightActions" />
            </template>
        </ToolbarComp>
        <ListComp
            ref="listComp"
            class="overflow-auto h-100"
            v-model="data"
            :search-text="searchText"
            :gui="useGuiStore().ContestantAndRaceContestantTime"
            :select-all="true"
            :hide-toolbar="true"
            :filter="[showAllTimesFilter, searchTextFilter]"
            :readonly="true"
            :custom-columns="handleCustomColumns"
            :row-class-rules="rowClassRules"
            :hide-statusbar="true"
            resizeStrategy="sizeColumnsToFit"
        />
    </ContentComp>
</template>
